import { useState } from 'react'
import { Project, BudgetStatus } from '../../../shared/types'
import { TimeEntry } from '../services/timeTracking'
import { calculateHourlyBudgetStatus } from '../utils/budgetUtils'

interface ProjectStatsProps {
  project?: Project
  timeEntries: TimeEntry[]
  getCurrentDuration: (entry: TimeEntry) => number
  formatDuration: (seconds: number) => string
}

export default function ProjectStats({ 
  project, 
  timeEntries, 
  getCurrentDuration, 
  formatDuration 
}: ProjectStatsProps) {
  const [showAllDays, setShowAllDays] = useState(false)

  if (!project) return null

  // Filter entries for this project
  const projectEntries = timeEntries.filter(entry => entry.projectId === project.id)
  
  // Calculate total hours
  const totalSeconds = projectEntries.reduce((total, entry) => {
    return total + getCurrentDuration(entry)
  }, 0)
  
  const totalHours = formatDuration(totalSeconds)

  // Generate daily data for the last 7 days or all project days
  const generateDailyData = () => {
    const days = []
    
    if (showAllDays) {
      // Get all unique dates from project entries
      const allDates = new Set<string>()
      projectEntries.forEach(entry => {
        const dateStr = new Date(entry.startTime).toISOString().split('T')[0]
        allDates.add(dateStr)
      })
      
      // Sort dates and take the last 30 days if there are many entries
      const sortedDates = Array.from(allDates).sort()
      const datesToShow = sortedDates.length > 30 ? sortedDates.slice(-30) : sortedDates
      
      datesToShow.forEach(dateStr => {
        const dayEntries = projectEntries.filter(entry => {
          const entryDate = new Date(entry.startTime).toISOString().split('T')[0]
          return entryDate === dateStr
        })
        
        const daySeconds = dayEntries.reduce((total, entry) => {
          return total + getCurrentDuration(entry)
        }, 0)
        
        const dayHours = daySeconds / 3600 // Convert to hours
        
        days.push({
          date: dateStr,
          hours: dayHours,
          hasEntries: dayEntries.length > 0
        })
      })
    } else {
      // Last 7 days view
      const today = new Date()
      
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today)
        date.setDate(today.getDate() - i)
        const dateStr = date.toISOString().split('T')[0]
        
        // Calculate hours for this day
        const dayEntries = projectEntries.filter(entry => {
          const entryDate = new Date(entry.startTime).toISOString().split('T')[0]
          return entryDate === dateStr
        })
        
        const daySeconds = dayEntries.reduce((total, entry) => {
          return total + getCurrentDuration(entry)
        }, 0)
        
        const dayHours = daySeconds / 3600 // Convert to hours
        
        days.push({
          date: dateStr,
          hours: dayHours,
          hasEntries: dayEntries.length > 0
        })
      }
    }
    
    return days
  }

  const dailyData = generateDailyData()
  const maxHours = Math.max(...dailyData.map(d => d.hours), 1) // At least 1 hour for scaling

  // Generate budget data if project has budget
  const generateBudgetData = () => {
    if (!project.budget || !project.budgetType) return null
    
    const days = []
    
    if (showAllDays) {
      // Get all unique dates from project entries
      const allDates = new Set<string>()
      projectEntries.forEach(entry => {
        const dateStr = new Date(entry.startTime).toISOString().split('T')[0]
        allDates.add(dateStr)
      })
      
      // Sort dates and take the last 30 days if there are many entries
      const sortedDates = Array.from(allDates).sort()
      const datesToShow = sortedDates.length > 30 ? sortedDates.slice(-30) : sortedDates
      
      datesToShow.forEach(dateStr => {
        const dayEntries = projectEntries.filter(entry => {
          const entryDate = new Date(entry.startTime).toISOString().split('T')[0]
          return entryDate === dateStr
        })

        const daySeconds = dayEntries.reduce((total, entry) => {
          return total + getCurrentDuration(entry)
        }, 0)

        let dayUsage = 0
        if (project.budgetType === 'hours') {
          dayUsage = daySeconds / 3600 // Hours
        } else if (project.budgetType === 'monetary' && project.hourlyRate) {
          dayUsage = (daySeconds / 3600) * project.hourlyRate // Money
        }

        // Calculate budget status for this day using shared utility
        const budgetStatus: BudgetStatus = project.budgetType === 'hours'
          ? calculateHourlyBudgetStatus(project, dayUsage)
          : 'in';

        days.push({
          date: dateStr,
          usage: dayUsage,
          budget: project.budget,
          budgetStatus, // NEW: Add status to day object
          hasEntries: dayEntries.length > 0
        })
      })
    } else {
      // Last 7 days view
      const today = new Date()
      
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today)
        date.setDate(today.getDate() - i)
        const dateStr = date.toISOString().split('T')[0]

        // Calculate budget usage for this day
        const dayEntries = projectEntries.filter(entry => {
          const entryDate = new Date(entry.startTime).toISOString().split('T')[0]
          return entryDate === dateStr
        })

        const daySeconds = dayEntries.reduce((total, entry) => {
          return total + getCurrentDuration(entry)
        }, 0)

        let dayUsage = 0
        if (project.budgetType === 'hours') {
          dayUsage = daySeconds / 3600 // Hours
        } else if (project.budgetType === 'monetary' && project.hourlyRate) {
          dayUsage = (daySeconds / 3600) * project.hourlyRate // Money
        }

        // Calculate budget status for this day using shared utility
        const budgetStatus: BudgetStatus = project.budgetType === 'hours'
          ? calculateHourlyBudgetStatus(project, dayUsage)
          : 'in';

        days.push({
          date: dateStr,
          usage: dayUsage,
          budget: project.budget,
          budgetStatus, // NEW: Add status to day object
          hasEntries: dayEntries.length > 0
        })
      }
    }
    
    return days
  }

  const budgetData = generateBudgetData()

  return (
    <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          <div
            className="w-1 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: project.color }}
          />
          <h3 className="text-sm font-medium text-gray-900 truncate" title={project.name}>{project.name}</h3>
        </div>
        <div className="text-sm text-gray-600">
          {totalHours}
        </div>
      </div>
      
      <div className="space-y-2">
        {/* Daily Hours Chart */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <button 
              onClick={() => setShowAllDays(!showAllDays)}
              className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              <svg 
                className={`w-3 h-3 transition-transform duration-200 ${showAllDays ? 'rotate-90' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span>{showAllDays ? 'All days' : 'Last 7 days'}</span>
            </button>
            <span className="text-xs text-gray-500">
              {budgetData ? 'Budget Usage' : 'Hours'}
            </span>
          </div>
          <div className="flex items-end space-x-0.5 h-8">
            {(budgetData || dailyData).map((day) => {
              // Type guard to check if this is budget data
              const isBudgetData = 'usage' in day && 'budget' in day;
              const height = isBudgetData
                ? Math.min((day.usage / day.budget) * 100, 100)
                : (day.hours / maxHours) * 100;

              // NEW: Determine color and opacity based on budget status
              let barColor = 'bg-blue-400';
              let opacity = 1;

              if (isBudgetData && 'budgetStatus' in day && day.budgetStatus) {
                if (day.budgetStatus === 'over') {
                  barColor = 'bg-red-400';
                  opacity = 1;
                } else if (day.budgetStatus === 'under') {
                  barColor = 'bg-red-400';
                  opacity = 0.4; // Dimmed/faded appearance
                } else {
                  barColor = 'bg-blue-400';
                  opacity = 1;
                }
              } else if (!day.hasEntries) {
                barColor = 'bg-gray-200';
                opacity = 1;
              }

              return (
                <div key={day.date} className="flex-1 flex flex-col items-center">
                  <div
                    className={`rounded-sm transition-all duration-200 ${barColor}`}
                    style={{
                      width: showAllDays ? '1px' : '100%',
                      height: `${Math.max(height, 2)}%`,
                      minHeight: day.hasEntries ? '4px' : '2px',
                      opacity // NEW: Apply opacity for dimmed effect
                    }}
                    title={`${new Date(day.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}: ${day.hasEntries ? (isBudgetData ? `${day.usage.toFixed(1)}/${day.budget} ${project.budgetType === 'hours' ? 'hours' : '€'}${('budgetStatus' in day && day.budgetStatus === 'over') ? ' (over budget)' : ('budgetStatus' in day && day.budgetStatus === 'under') ? ' (under budget)' : ''}` : `${day.hours.toFixed(1)}h`) : 'No entries'}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Budget Info */}
        {budgetData && project.budget && (
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              Budget: {project.budgetType === 'hours'
                ? `${project.budget} hours/day ± ${((project.budgetHourLimit || 60) / 60).toFixed(1)} hours`
                : `€${project.budget.toLocaleString('de-DE')}`
              }
            </span>
            <span>
              {project.hourlyRate && project.budgetType === 'monetary' && (
                `€${project.hourlyRate}/h`
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  )
} 